// Llamada autenticada a la API de la nomina, compartida por el cliente de la
// nomina y el de documentos (PP-23): token en el header, sin cookies ni
// redirecciones, y el error tipado por status sin pintar nunca el `detail`
// remoto (puede traer datos de personas).
import { ErrorResponse } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { FleetError, type FleetSite } from "./port.js";

export type Method = "GET" | "POST" | "PATCH" | "DELETE";

function failure(status: number, body: unknown): FleetError {
  if (status === 401 || status === 403) return new FleetError("access");
  if (status === 404) return new FleetError("not_found");
  if (status === 400) return new FleetError("validation");
  if (status === 409) {
    // Solo el `type` del sobre, nunca el `detail`.
    const envelope = ErrorResponse.safeParse(body);
    return new FleetError(envelope.success && envelope.data.error.type === "subcontract_missing" ? "subcontract_missing" : "conflict");
  }
  return new FleetError("unavailable");
}

/** Lo minimo de un schema de contracts, sin depender de zod desde el portal. */
export interface Schema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

export function parseOrFail<T>(schema: Schema<T>, value: unknown, kind: "validation" | "unavailable"): T {
  const result = schema.safeParse(value);
  if (!result.success) throw new FleetError(kind);
  return result.data;
}

/** Una llamada autenticada a la API; el error sale tipado y sin el cuerpo remoto. */
export type FleetCall = (session: PortalSession, method: Method, path: string, body?: unknown) => Promise<unknown>;

export function createFleetCall(apiUrl: string, request: typeof fetch = fetch): FleetCall {
  const base = apiUrl.replace(/\/+$/, "");

  return async function call(session, method, path, body) {
    let response: Response;
    try {
      response = await request(`${base}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      });
    } catch {
      throw new FleetError("unavailable");
    }
    const payload: unknown = await response.json().catch(() => undefined);
    if (!response.ok) throw failure(response.status, payload);
    return payload;
  };
}

/** Path de la planta, con el id escapado: la planta viaja en cada llamada. */
export const sitePath = (s: FleetSite): string => `/sites/${encodeURIComponent(s.siteId)}`;

